import React, { useState } from 'react';
import { ChevronDown, Store } from 'lucide-react';

import MagasinBadge from '../../components/MagasinBadge';
import { usePermissions } from '../../contexts/PermissionsContext';

/**
 * Sélecteur compact de magasin de session POS V2.
 * Super-admin et admin_magasin peuvent encaisser sur tout magasin actif.
 */
const PosV2SessionMagPicker = ({ sessionMag, onChange, cartCount = 0 }) => {
  const { allMagasins, isSuper, isAdminMag, adminAccess, isCommercialMode } = usePermissions();
  const [open, setOpen] = useState(false);

  const candidates = allMagasins || [];
  if (candidates.length <= 1) {
    return <MagasinBadge code={sessionMag} size="md" />;
  }

  const handlePick = (code) => {
    if (cartCount > 0 && code !== sessionMag) {
      const ok = window.confirm(
        `Panier en cours sur ${sessionMag}. Basculer sur ${code} pour cette vente ?`
      );
      if (!ok) {
        setOpen(false);
        return;
      }
    }
    onChange(code);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="pos-v2-session-mag-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-xs font-semibold text-white min-h-[32px]"
        title="Magasin d'encaissement"
      >
        <Store size={14} className="text-red-400 shrink-0" />
        <MagasinBadge code={sessionMag} size="sm" />
        <ChevronDown size={12} className="text-gray-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] rounded-xl border border-white/10 bg-[#151820] shadow-2xl py-1 overflow-hidden">
            <p className="px-3 py-2 text-[10px] text-gray-500 border-b border-white/10">
              Magasin d&apos;encaissement (caisse + ticket)
            </p>
            {candidates.map((m) => {
              const selected = m.code === sessionMag;
              const commercial = isAdminMag && isCommercialMode(m.code);
              const admin = isSuper || adminAccess.includes(m.code);
              return (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => handlePick(m.code)}
                  data-testid={`pos-v2-session-mag-${m.code}`}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 min-h-[44px] text-sm hover:bg-white/5 text-left ${
                    selected ? 'bg-red-950/40 text-white font-semibold' : 'text-gray-200'
                  }`}
                >
                  <MagasinBadge code={m.code} size="sm" />
                  <span className="truncate flex-1">{m.name || m.code}</span>
                  {admin && !commercial && (
                    <span className="text-[9px] text-emerald-400/80 shrink-0">admin</span>
                  )}
                  {commercial && (
                    <span className="text-[9px] text-amber-400/80 shrink-0">vente</span>
                  )}
                  {selected && <span className="text-emerald-400 text-xs shrink-0">✓</span>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PosV2SessionMagPicker;
